/* Created by Interactive 63 - https://interactive63.com */
/**
 * One animatable property in the inspector: keyframe diamond, label, value editor, and
 * previous/next keyframe arrows. It reads values at the playhead and writes edits through the
 * store's property actions.
 */
import type { ElementId, PropertyValue } from '../../../core/model/types';
import { findTrack, keyframesAt, adjacentKeyframeTime } from '../../../core/timeline/keyframes';
import type { PropertyDefinition } from '../../../core/properties/registry';
import { selectKeyframes } from '../../store/actions';
import { cascadeTargets } from '../../../core/properties/cascade';
import { drawOn, setPropertyValue, toggleKeyframeAtPlayhead } from '../../store/property-actions';
import { h } from '../../ui/dom';
import { icon } from '../../ui/icons';
import { jsBadge, MORPH_JS_TITLE } from '../../ui/js-badge';
import { toast } from '../../ui/toast';
import type { InspectorContext } from './inspector-context';
import { applyPatch, createValueEditor, type ValueEditor } from './value-editor';

type DiamondState = 'none' | 'track' | 'key';

// The repeat notice is shown once per session.
let mappedNoticeShown = false;

export class PropertyRow {
  readonly el: HTMLElement;
  private readonly diamond: HTMLButtonElement;
  private readonly previous: HTMLButtonElement;
  private readonly next: HTMLButtonElement;
  private readonly editor: ValueEditor;
  private lastRejection = 0;

  /** Builds the row for a property on the selected elements. */
  constructor(
    private readonly ctx: InspectorContext,
    private readonly definition: PropertyDefinition,
    private readonly elementIds: readonly ElementId[],
  ) {
    this.diamond = h('button', { type: 'button', class: 'keyframe-toggle' });
    this.diamond.append(icon('stop'));
    this.previous = h(
      'button',
      {
        type: 'button',
        class: 'key-nav',
        'aria-label': `Previous ${definition.label} keyframe`,
        title: 'Previous keyframe',
      },
      '‹',
    );
    this.next = h(
      'button',
      {
        type: 'button',
        class: 'key-nav',
        'aria-label': `Next ${definition.label} keyframe`,
        title: 'Next keyframe',
      },
      '›',
    );

    this.editor = createValueEditor(definition, {
      scrubKey: `scrub:${elementIds.join(',')}:${definition.key}`,
      documentColours: ctx.documentColours,
      onChange: (patch, key) => {
        const outcome = setPropertyValue(
          ctx.store,
          this.targets(),
          definition.key,
          (id) => applyPatch(ctx.readValue(id, definition.key), patch),
          {
            time: ctx.time(),
            coalesceKey: key,
            onMappedToCycle: () => {
              if (!mappedNoticeShown) {
                mappedNoticeShown = true;
                toast.info('This track repeats, so the change was applied to the first cycle.');
              }
            },
          },
        );
        if (outcome === 'rejected') this.explainRejection();
        this.refresh(true);
      },
      onDrawOn: () => {
        drawOn(ctx.store, elementIds, ctx.time());
      },
      onSetShape:
        definition.valueType === 'morph' && elementIds.length === 1
          ? (anchor) => {
              ctx.shapes.menu(elementIds[0] ?? '', anchor);
            }
          : undefined,
    });

    const label = h(
      'span',
      { class: 'property-label', title: definition.description },
      definition.label,
      definition.cssOnly ? null : jsBadge(MORPH_JS_TITLE),
      definition.browserNote
        ? h(
            'span',
            {
              class: 'info-badge',
              title: definition.browserNote,
              'aria-label': definition.browserNote,
            },
            'ⓘ',
          )
        : null,
    );
    this.el = h(
      'div',
      {
        class: 'property-row',
        'data-property': definition.key,
      },
      this.diamond,
      label,
      this.editor.el,
      h('span', { class: 'key-nav-group' }, this.previous, this.next),
    );
    this.bind();
    this.refresh();
  }

  /** The elements this row reads and writes: a group's colour and stroke edits reach its shapes. */
  private targets(): ElementId[] {
    return cascadeTargets(this.ctx.store.getState().project, this.elementIds, this.definition.key);
  }

  /** Brings the diamond, arrows and value display up to date with the project and playhead. */
  refresh(force = false): void {
    const { project } = this.ctx.store.getState();
    const time = this.ctx.time();
    const playing = this.ctx.isPlaying();

    const values: PropertyValue[] = this.elementIds.map((id) =>
      this.ctx.readValue(id, this.definition.key),
    );
    this.editor.show(values, force);
    this.editor.setReadOnly(playing);

    this.setDiamond(this.diamondState(time));
    this.diamond.disabled = playing;

    const before = adjacentKeyframeTime(
      project,
      this.targets(),
      time,
      'previous',
      this.definition.key,
    );
    const after = adjacentKeyframeTime(project, this.targets(), time, 'next', this.definition.key);
    this.previous.disabled = before === undefined;
    this.next.disabled = after === undefined;
    this.previous.dataset['time'] = String(before ?? '');
    this.next.dataset['time'] = String(after ?? '');
  }

  /** Works out whether the elements have no track, a track, or keyframes at the playhead. */
  private diamondState(time: number): DiamondState {
    const { project } = this.ctx.store.getState();
    const tracks = this.targets().map((id) => findTrack(project, id, this.definition.key));
    if (tracks.every((t) => !t)) return 'none';
    const allKeyed = this.targets().every((id, i) => {
      const track = tracks[i];
      return (
        track !== undefined &&
        keyframesAt(project, id, time).some((kf) => track.keyframes.includes(kf))
      );
    });
    return allKeyed ? 'key' : 'track';
  }

  /** Applies the diamond's state to its classes and accessible label. */
  private setDiamond(state: DiamondState): void {
    this.diamond.classList.toggle('is-none', state === 'none');
    this.diamond.classList.toggle('is-track', state === 'track');
    this.diamond.classList.toggle('is-key', state === 'key');
    const text = state === 'key' ? 'Remove keyframe' : 'Add keyframe';
    this.diamond.setAttribute('aria-label', `${text}: ${this.definition.label}`);
    this.diamond.title =
      state === 'key' ? 'Remove keyframe (Alt + click selects it)' : 'Add keyframe';
    this.diamond.setAttribute('aria-pressed', String(state === 'key'));
  }

  /** Explains why an edit to an animated property was ignored, without repeating too often. */
  private explainRejection(): void {
    const now = Date.now();
    if (now - this.lastRejection < 2000) return;
    this.lastRejection = now;
    toast.info('Auto-key is off. Click the diamond to add a keyframe.');
  }

  /** Wires the diamond and the previous/next arrows. */
  private bind(): void {
    this.diamond.addEventListener('click', (event) => {
      const time = this.ctx.time();
      if (event.altKey && this.diamond.classList.contains('is-key')) {
        const { project } = this.ctx.store.getState();
        const ids = this.targets().flatMap((id) => {
          const track = findTrack(project, id, this.definition.key);
          return keyframesAt(project, id, time)
            .filter((kf) => track?.keyframes.includes(kf))
            .map((kf) => kf.id);
        });
        selectKeyframes(this.ctx.store, ids);
        return;
      }
      if (
        this.definition.key === 'morph' &&
        !this.elementIds.every((id) => this.ctx.shapes.ensurePath(id))
      ) {
        return;
      }
      toggleKeyframeAtPlayhead(
        this.ctx.store,
        this.targets(),
        this.definition.key,
        time,
        this.ctx.readValue,
      );
      this.refresh();
    });
    for (const button of [this.previous, this.next]) {
      button.addEventListener('click', () => {
        const target = Number(button.dataset['time']);
        if (button.dataset['time'] !== '' && Number.isFinite(target))
          this.ctx.playback.seek(target);
      });
    }
  }
}
